import {
  Box,
  Button,
  FormControl,
  Input,
  Link,
  List,
  ListItem,
  Tooltip,
  Typography,
} from '@mui/material';
import {useIsLargeDevice} from '@jobseeker/lotus/hooks';
import {Dispatch, SetStateAction, useState} from 'react';

import {Recommendation} from '@jobseeker/common-core/services/types';

const ABS_URL = 'https://static.iimjobs.com/';

interface RecommendationsSectionProps {
  recommendations: Recommendation[];
  handleSendRecommendationRequest: (email: string) => Promise<string>;
  setPopUpRecommendation: Dispatch<SetStateAction<Recommendation | null>>;
}

const isValidEmail = (email: string) => {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
};

const RecommendationSection = ({
  recommendations,
  handleSendRecommendationRequest,
  setPopUpRecommendation,
}: RecommendationsSectionProps) => {
  const isLarge = useIsLargeDevice(768);

  let recommList1: Recommendation[] = [];
  if (recommendations && recommendations.length > 0) {
    recommList1 = recommendations
      .filter(r => r.status === 0 || r.status === 1 || r.status === 3)
      .reverse();
  }

  const [inputEmail, setInputEmail] = useState<string>('');
  const [error, setError] = useState<string>('');
  const [hasTouched, setHasTouched] = useState<boolean>(false);
  const [sendRequestMsgType, setSendRequestMsgType] = useState<string>('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setHasTouched(true);
    if (!inputEmail) {
      setError('Please enter an email.');
      return;
    }
    if (!isValidEmail(inputEmail)) {
      setError('Please enter a valid email.');
      return;
    }
    setError('');
    const res = await handleSendRecommendationRequest(inputEmail);
    setSendRequestMsgType(res);
    setInputEmail('');
    setHasTouched(false);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setInputEmail(e.target.value);
    if (hasTouched) {
      if (!e.target.value) {
        setError('Please enter an email.');
      } else if (!isValidEmail(e.target.value)) {
        setError('Please enter a valid email.');
      } else {
        setError('');
      }
    }
  };

  return (
    <Box
      sx={{
        width: '100%',
        minHeight: '635px',
        bgcolor: '#fff',
        p: isLarge ? '15px 10px 15px 15px' : '0px 15px',
      }}
    >
      <Box sx={{m: 0, p: 0, borderBottom: '1px solid #ececec'}}>
        <Box sx={{p: '15px 0px'}}>
          <Typography
            sx={{
              color: '#4b4b4b',
              fontSize: 16,
              p: '8px 0px 20px 45px',
              background: `url(${ABS_URL}resources/img/srecnew1.png) left 4px no-repeat`,
              wordBreak: 'break-all',
            }}
          >
            Send <span style={{color: '#999'}}>a Recommendation</span>
          </Typography>
          <Typography sx={{m: '0px 0px 10px', fontSize: 12, color: '#585858'}}>
            Enhance your profile with recommendations of your work and skills.
            Candidates with recommendations have higher likelihood of being
            shortlisted.
          </Typography>
          {sendRequestMsgType !== '' && (
            <Typography
              sx={{
                color: '#fff',
                bgcolor:
                  sendRequestMsgType === 'Request Sent.' ? '#996' : '#b86363',
                p: '5px 17px',
                fontSize: 13,
                mb: '20px',
                borderRadius: '4px',
              }}
            >
              {sendRequestMsgType}
              <Typography
                component={'span'}
                sx={{
                  fontSize: 21,
                  lineHeight: 1,
                  float: 'right',
                  cursor: 'pointer',
                  fontWeight: 400,
                }}
                onClick={() => setSendRequestMsgType('')}
              >
                x
              </Typography>
            </Typography>
          )}

          <FormControl variant="standard" sx={{m: 0, width: '100%'}}>
            <Typography sx={{fontSize: 14, color: '#4B4B4B', m: '0px 0px 5px'}}>
              Request a recommendation from -
            </Typography>
            <Input
              id="email"
              name="email"
              value={inputEmail}
              onChange={handleInputChange}
              required
              placeholder="Email"
              sx={{
                border: '1px solid #ccc',
                p: '6px 30px 6px 12px',
                fontSize: 14,
                color: error ? '#d00a42' : '#555',
                background: '#fff',
                lineHeight: 1.428571429,
                height: '34px',
                '&:before': {
                  borderBottom: 'none',
                },
                '&:after': {
                  borderBottom: 'none',
                },
                '&:hover:not(.Mui-disabled, .Mui-error):before': {
                  borderBottom: 'none !important',
                },
                '&.Mui-focused': {
                  border: '1px solid #66afe9',
                },
                '& input::placeholder': {
                  color: '#888',
                  opacity: 1,
                },
              }}
            />
            {error && (
              <Typography sx={{fontSize: 14, color: '#d00a42', m: 0}}>
                {error}
              </Typography>
            )}
            <Button
              variant="contained"
              sx={{
                width: '100%',
                borderRadius: 0,
                bgcolor: '#ebebeb',
                color: '#4b4b4b',
                fontSize: 13,
                p: '7px 25px',
                mt: '12px',
                textShadow: 'none',
                '&:hover': {
                  bgcolor: '#ebebeb',
                },
              }}
              onClick={handleSubmit}
            >
              Send
            </Button>
          </FormControl>
        </Box>
      </Box>
      <Box sx={{m: 0, p: 0, borderBottom: '1px solid #ececec'}}>
        {recommList1 && recommList1.length > 0 && (
          <Box sx={{m: 0, p: '15px 0px'}}>
            <Typography
              sx={{
                color: '#4b4b4b',
                fontSize: 16,
                p: '8px 0px 15px 45px',
                background: `url(${ABS_URL}resources/img/reccmdeticnew1.png) left 4px no-repeat`,
                wordBreak: 'break-all',
              }}
            >
              Recommendations
            </Typography>
            <List style={{listStyle: 'none', margin: 0, padding: 0}}>
              {recommList1.map((recommendation, index) => {
                let recommTitle = '';
                let statusIcon = '';
                if (recommendation?.status === 1) {
                  recommTitle = 'Approve Recommendation';
                  statusIcon = `${ABS_URL}resources/img/pending.png`;
                } else if (recommendation?.status === 3) {
                  recommTitle = 'Rejected Recommendation';
                  statusIcon = `${ABS_URL}resources/img/rejected.png`;
                } else {
                  recommTitle = 'Pending Recommendation';
                  statusIcon = '';
                }

                return (
                  <ListItem
                    key={index}
                    sx={{
                      background: `url(${ABS_URL}resources/img/srcar.png) right 10px no-repeat`,
                      p: '6px 40px 2px 0px',
                    }}
                    onClick={() => setPopUpRecommendation(recommendation)}
                  >
                    <Tooltip
                      placement="bottom"
                      title={
                        <Typography
                          variant="subtitle2"
                          sx={{
                            textAlign: 'center',
                            whiteSpace: 'normal',
                            overflowWrap: 'break-word',
                            backgroundColor: 'black',
                            color: '#fff',
                            px: '4px',
                            borderRadius: '4px',
                            fontSize: 10,
                            fontWeight: 400,
                          }}
                        >
                          {recommTitle}
                        </Typography>
                      }
                      arrow
                      componentsProps={{
                        tooltip: {
                          sx: {
                            backgroundColor: 'black',
                            color: '#fff',
                            p: '4px',
                          },
                        },
                        arrow: {
                          sx: {
                            color: 'black',
                          },
                        },
                      }}
                    >
                      <Box
                        sx={{
                          width: '100%',
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                        }}
                      >
                        <span
                          style={{
                            display: 'inline-block',
                            fontSize: 14,
                            color: '#999999',
                            wordBreak: 'break-all',
                          }}
                        >
                          {recommendation?.email}
                        </span>
                        <span
                          style={{
                            display: 'inline-block',
                            marginRight: '-28px',
                            background: `url(${statusIcon}) no-repeat`,
                            backgroundSize: 'contain',
                            marginLeft: 'auto',
                            width: 16,
                            height: 16,
                          }}
                        />
                      </Box>
                    </Tooltip>
                  </ListItem>
                );
              })}
            </List>
            <Link
              sx={{
                mt: 10,
                fontSize: 14,
                color: '#4B4B4B',
                textDecoration: 'none',
                '&:hover': {
                  textDecoration: 'underline',
                },
              }}
              href="pending-recommendations/"
              target="_blank"
            >
              More Details ...
            </Link>
          </Box>
        )}
      </Box>
      <Box sx={{m: 0, p: 0}}>
        <Box sx={{m: 0, p: '15px 0px'}}>
          <Typography
            sx={{
              color: '#4b4b4b',
              fontSize: 16,
              p: '8px 0px 15px 45px',
              background: `url(${ABS_URL}resources/img/prwsetnew1.png) left 4px no-repeat`,
              wordBreak: 'break-all',
            }}
          >
            Privacy <span style={{color: '#999'}}>Settings</span>
          </Typography>
          <ul style={{listStyle: 'none', margin: 0, padding: 0}}>
            <li
              style={{
                padding: '3px 3px 5px 20px',
                color: '#989898',
                background: `url(${ABS_URL}resources/img/setting.png) left 10px no-repeat`,
              }}
            >
              <Link
                href="/settings"
                target="_blank"
                sx={{
                  color: '#4c4c4c',
                  fontSize: 13,
                  wordBreak: 'break-all',
                  textDecoration: 'none',
                  '&:hover': {
                    textDecoration: 'underline',
                  },
                }}
              >
                Settings
              </Link>
            </li>
            <li
              style={{
                padding: '3px 3px 5px 20px',
                color: '#989898',
                background: `url(${ABS_URL}resources/img/block.png) left 10px no-repeat`,
              }}
            >
              <Link
                href="settings?section=privacy-settings"
                target="_blank"
                sx={{
                  color: '#4c4c4c',
                  fontSize: 13,
                  wordBreak: 'break-all',
                  textDecoration: 'none',
                  '&:hover': {
                    textDecoration: 'underline',
                  },
                }}
              >
                Block Companies
              </Link>
            </li>
          </ul>
        </Box>
      </Box>
    </Box>
  );
};

export default RecommendationSection;
